import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Text } from '@reservamos/elements';
import cn from 'utils/cn';
import slideTicket from 'images/utilities/slide-1-ticket.webp';
import slideEmail from 'images/utilities/slide-2-email.webp';

const SLIDE_INTERVAL_MS = 6000;

/**
 * Where the data the form asks for can be found. The artwork is the funnel's own, not a tenant's:
 * it illustrates a ticket and an inbox, which every brand's traveller has.
 *
 * The first caption names that data, so it is the caller's to choose — a form asking for a ticket
 * number must not be told to look for an operation number it never mentions.
 * @param {string} ticketCaptionKey - Copy key naming what the ticket carries.
 * @returns {Array<object>} The slides, in order.
 */
const buildSlides = (ticketCaptionKey) => [
  {
    id: 'ticket',
    src: slideTicket,
    titleKey: 'form_slides.where_to_find',
    captionKey: ticketCaptionKey,
  },
  {
    id: 'email',
    src: slideEmail,
    captionKey: 'form_slides.in_your_email',
  },
];

const propTypes = {
  ticketCaptionKey: PropTypes.string,
};

/**
 * ExchangeFormSlides component.
 *
 * The panel beside the exchange form: one illustration at a time, with a caption and a dot per
 * slide. It advances on its own and can be driven by the dots.
 *
 * It holds no colours of its own — the text is white over whatever the shell paints behind it, so
 * the panel takes the tenant's brand colour with no per-brand code here.
 *
 * @param {object} props - Component props.
 * @param {string} [props.ticketCaptionKey] - Copy key for the first slide's caption, naming the
 *   data the rendered form actually asks for.
 * @returns {JSX.Element}
 */
const ExchangeFormSlides = ({ ticketCaptionKey = 'form_slides.on_your_ticket' }) => {
  const { t } = useTranslation('exchange');
  const [activeIndex, setActiveIndex] = useState(0);
  const slides = useMemo(() => buildSlides(ticketCaptionKey), [ticketCaptionKey]);

  useEffect(() => {
    // A carousel that moves by itself is motion the traveller did not ask for. Where the system
    // says to reduce it, the slides stay put and the dots remain the way through them.
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return undefined;

    const timer = window.setInterval(
      () => setActiveIndex((current) => (current + 1) % slides.length),
      SLIDE_INTERVAL_MS,
    );
    return () => window.clearInterval(timer);
    // `activeIndex` restarts the count on every turn, so a slide picked from the dots stays for a
    // full interval instead of being swapped away by a tick that was already due.
  }, [slides.length, activeIndex]);

  return (
    <div className="flex h-full w-full flex-col items-center justify-between gap-4 px-8 pt-8 pb-10 md:gap-6 md:py-10">
      {/* Every slide stays mounted and stacked, so the panel keeps one height and the images are
          not re-fetched on each turn. Only the active one is visible, and to a screen reader only
          the active one exists. */}
      <div className="relative flex min-h-[260px] w-full flex-1 items-center justify-center md:min-h-[320px]">
        {slides.map((slide, index) => {
          const isActive = index === activeIndex;

          return (
            <div
              key={slide.id}
              aria-hidden={!isActive}
              className={cn(
                'absolute inset-0 flex flex-col items-center justify-center gap-6 text-center transition-opacity duration-500 ease-out',
                isActive ? 'opacity-100' : 'pointer-events-none opacity-0',
              )}
            >
              {Boolean(slide.titleKey) && (
                <Text size="L" weight="bold" color="white">
                  {t(slide.titleKey)}
                </Text>
              )}
              <img
                src={slide.src}
                alt=""
                className="max-h-[150px] w-auto max-w-[220px] object-contain md:max-h-[260px] md:max-w-[300px]"
                loading={index === 0 ? 'eager' : 'lazy'}
                width="300"
                height="260"
              />
              <Text weight="bold" color="white">
                {t(slide.captionKey)}
              </Text>
            </div>
          );
        })}
      </div>

      <div className="flex flex-shrink-0 items-center gap-2">
        {slides.map((slide, index) => {
          const isActive = index === activeIndex;

          return (
            <button
              key={slide.id}
              type="button"
              aria-label={t(slide.captionKey)}
              aria-current={isActive}
              onClick={() => setActiveIndex(index)}
              className={cn(
                'h-2.5 w-2.5 cursor-pointer rounded-full border-0 p-0 transition-colors duration-200',
                isActive ? 'bg-white' : 'bg-white/40',
              )}
            />
          );
        })}
      </div>
    </div>
  );
};

ExchangeFormSlides.propTypes = propTypes;

export default ExchangeFormSlides;
